/**
 * screens/settings/audit.tsx — Audit Log (P-008), ADMIN-only.
 * Faithful RN port of design/security.jsx AuditPage: full-text search, category
 * chips, append-only notice, and CSV export of the filtered view. Reads the live
 * log via platformApi.getAuditLog (falls back to the seeded events offline).
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable, Platform } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Panel, DataTable, Tag } from '../../components/platform/primitives';
import { PButton, PTextInput } from '../../components/platform/controls';
import Icon from '../../components/icon';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, Tone } from '../../utils/platformTokens';
import { getAuditLog } from '../../services/platform/platformApi';

const AUDIT_CATS = ['login', 'invitation', 'plan', 'deletion'];
const CAT_TONE: Record<string, Tone> = { login: 'neutral', invitation: 'info', plan: 'accent', deletion: 'danger' };

type Event = { id: string; ts: string; actor: string; cat: string; detail: string };

const AuditScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, role } = usePlatform();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [cats, setCats] = useState<string[]>([]);
  const [events, setEvents] = useState<Event[]>(state.audit);

  useEffect(() => {
    getAuditLog()
      .then((live) => {
        if (Array.isArray(live) && live.length) {
          setEvents(live.map((e: any) => ({
            id: e.id, ts: e.occurred_at ?? e.ts, actor: e.actor ?? e.actor_id ?? '—',
            cat: e.action ?? e.cat ?? 'login', detail: e.detail ?? e.entity ?? '',
          })));
        }
      })
      .catch(() => undefined);
  }, []);

  if (role !== 'ADMIN') return <PermissionDenied />;

  const filtered = events.filter((e) => {
    if (q && !`${e.actor} ${e.detail} ${e.id}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (cats.length && !cats.includes(e.cat)) return false;
    return true;
  });
  const toggleCat = (cat: string) => setCats((p) => (p.includes(cat) ? p.filter((x) => x !== cat) : [...p, cat]));

  const exportCsv = () => {
    const rows = [['Timestamp', 'Actor', 'Category', 'Detail', 'Event ID'], ...filtered.map((e) => [e.ts, e.actor, e.cat, e.detail, e.id])];
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    if (Platform.OS === 'web') {
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'audit-log-filtered.csv'; a.click(); URL.revokeObjectURL(url);
    }
    toast.success(`Exported ${filtered.length} filtered events.`);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="Audit Log"
        sub="An immutable, read-only record of everything that happens in your workspace."
        action={<PButton icon="download" onPress={exportCsv}>Export CSV (filtered)</PButton>}
      />

      <Panel>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <View style={{ flex: 1, minWidth: 200 }}>
            <PTextInput leadingIcon="search" placeholder="Search actor, detail or event ID…" value={q} onChangeText={setQ} />
          </View>
          <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
            {AUDIT_CATS.map((cat) => {
              const on = cats.includes(cat);
              return (
                <Pressable key={cat} onPress={() => toggleCat(cat)}>
                  <Tag tone={on ? 'accent' : 'neutral'} size="lg">{cat}</Tag>
                </Pressable>
              );
            })}
          </View>
        </View>
      </Panel>

      <Panel bodyStyle={{ padding: 0 }}>
        <DataTable<Event>
          minWidth={860}
          keyExtractor={(e) => e.id}
          rows={filtered}
          empty={<Text style={{ color: c.ink3 }}>No events match your filters.</Text>}
          columns={[
            { key: 'ts', header: 'Timestamp', width: 170, render: (e) => <Text style={{ fontFamily: theme.font.mono, color: c.ink2, fontSize: FS.sm }}>{e.ts}</Text> },
            { key: 'actor', header: 'Actor', width: 150, render: (e) => <Text style={{ color: c.ink, fontSize: FS.md }}>{e.actor}</Text> },
            { key: 'cat', header: 'Category', width: 120, render: (e) => <Tag tone={CAT_TONE[e.cat] || 'neutral'}>{e.cat}</Tag> },
            { key: 'detail', header: 'Detail', render: (e) => <Text style={{ color: c.ink2, fontSize: FS.md }}>{e.detail}</Text> },
            { key: 'id', header: 'Event ID', width: 120, render: (e) => <Text style={{ fontFamily: theme.font.mono, color: c.ink3, fontSize: FS.xs }}>{e.id}</Text> },
          ]}
        />
      </Panel>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 12 }}>
        <Icon name="lock" size={13} color={c.ink3} />
        <Text style={{ fontSize: FS.sm, color: c.ink3 }}>
          Records are append-only and can't be edited or deleted. Showing {filtered.length} of {events.length} events.
        </Text>
      </View>
    </ScrollView>
  );
};

export default AuditScreen;
